<script setup lang="ts">
/* eslint-env vue/setup-compiler-macros */
import { ref, computed, watch } from 'vue';
import Popover from 'primevue/popover';

interface Props {
    modelValue: string[]
    options: string[]
    label?: string
}

const props = withDefaults(defineProps<Props>(), {
    label: 'Filter',
});

const emit = defineEmits<{
    'update:modelValue': [value: string[]]
}>();

const popoverRef = ref();

const normalizedSelected = computed(() => props.modelValue.filter((value) => props.options.includes(value)));
const allSelected = computed(() => props.options.length > 0 && normalizedSelected.value.length === props.options.length);
// Selections are staged in a draft so the panel filters and closes only on Apply.
const draft = ref<string[]>([]);
const hasSelection = computed(() => draft.value.length > 0);

function syncDraft() {
    draft.value = [...normalizedSelected.value];
}

const triggerValue = computed(() => {
    if (allSelected.value) return 'All';
    if (normalizedSelected.value.length === 0) return 'All';
    return `${normalizedSelected.value.length} of ${props.options.length}`;
});

function isChecked(option: string): boolean {
    return draft.value.includes(option);
}

function toggleOption(option: string) {
    draft.value = draft.value.includes(option)
        ? draft.value.filter((value) => value !== option)
        : [...draft.value, option];
}

function clearSelection() {
    draft.value = [];
}

function applySelection() {
    emit('update:modelValue', [...draft.value]);
    popoverRef.value?.hide();
}

function cancelSelection() {
    syncDraft();
    popoverRef.value?.hide();
}

// Refreshed before the panel opens rather than on the popover's show event, so the first frame
// already holds the applied selection.
function togglePanel(event: MouseEvent) {
    if (!popoverRef.value?.visible) syncDraft();
    popoverRef.value?.toggle(event);
}

watch(
    () => props.options,
    (options) => {
        const next = props.modelValue.filter((value) => options.includes(value));
        if (next.length !== props.modelValue.length) {
            emit('update:modelValue', next);
        }
    },
    { immediate: true },
);
</script>

<template>
    <div class="checkbox-filter">
        <button
            class="checkbox-filter__trigger"
            type="button"
            aria-haspopup="dialog"
            @click="togglePanel"
        >
            <span class="checkbox-filter__trigger-label">{{ label }}:</span>
            <span class="checkbox-filter__trigger-value">{{ triggerValue }}</span>
            <svg class="checkbox-filter__trigger-chevron" width="12" height="7" viewBox="0 0 12 7" fill="none" aria-hidden="true">
                <path d="M1 1L6 6L11 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
        </button>

        <Popover
            ref="popoverRef"
            :pt="{ root: { class: 'checkbox-filter-popover popover-align-end' } }"
        >
            <button
                type="button"
                class="checkbox-filter__clear"
                :disabled="!hasSelection"
                @click="clearSelection"
            >
                Clear All / Display All
            </button>
            <div class="checkbox-filter__separator"></div>
            <div class="checkbox-filter__options">
                <label
                    v-for="option in options"
                    :key="option"
                    class="checkbox-filter__option"
                    @click.prevent="toggleOption(option)"
                >
                    <span class="checkbox-filter__checkmark" :class="{ 'checkbox-filter__checkmark--checked': isChecked(option) }">
                        <svg v-if="isChecked(option)" width="10" height="8" viewBox="0 0 10 8" fill="none">
                            <path d="M1 4L3.5 6.5L9 1" stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </span>
                    <input
                        class="checkbox-filter__input"
                        type="checkbox"
                        tabindex="-1"
                        :checked="isChecked(option)"
                    />
                    <span class="checkbox-filter__option-label">{{ option }}</span>
                </label>
            </div>
            <div class="checkbox-filter__footer">
                <button
                    type="button"
                    class="checkbox-filter__footer-action checkbox-filter__footer-action--secondary"
                    @click="cancelSelection"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    class="checkbox-filter__footer-action checkbox-filter__footer-action--primary"
                    @click="applySelection"
                >
                    Apply
                </button>
            </div>
        </Popover>
    </div>
</template>

<style lang="scss" scoped>
.checkbox-filter {
    position: static;

    &__trigger {
        display: inline-flex;
        align-items: center;
        justify-content: flex-start;
        gap: 6px;
        height: 46px;
        padding: 0 20px;
        border: 1px solid $border-gray-color;
        border-radius: 10px;
        background: $white-color;
        font-family: $font-family-archivo;
        font-size: 16px;
        font-weight: 400;
        line-height: 17px;
        color: $black-color;
        text-align: left;
        white-space: nowrap;
        cursor: pointer;
    }

    &__trigger-label {
        color: rgba($black-color, 0.7);
    }

    &__trigger-value {
        color: $black-color;
        font-weight: 500;
    }

    &__trigger-chevron {
        color: rgba(0, 0, 0, .6) !important;
        flex-shrink: 0;
        margin-left: 2px;
    }

    &__clear {
        width: 100%;
        border: 0;
        background: transparent;
        text-align: left;
        padding: 8px 18px 10px;
        font-family: $font-family-archivo;
        font-size: 14px;
        font-weight: 600;
        color: $black-color;
        cursor: pointer;

        &:hover {
            background: rgba($main-orange, 0.05);
        }

        &:disabled {
            color: rgba($black-color, 0.4);
            cursor: not-allowed;
        }

        &:disabled:hover {
            background: transparent;
        }
    }

    &__separator {
        height: 1px;
        background: $border-gray-color;
        margin: 2px 0 6px;
    }

    &__options {
        max-height: 240px;
        overflow-y: auto;
        padding: 4px 0;
    }

    &__option {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 8px 18px;
        cursor: pointer;
        transition: background 0.12s;

        &:hover {
            background: rgba($main-orange, 0.05);
        }
    }

    &__input {
        position: absolute;
        opacity: 0;
        width: 0;
        height: 0;
        pointer-events: none;
    }

    &__checkmark {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 18px;
        height: 18px;
        border-radius: 5px;
        border: 1.5px solid $border-gray-color;
        background: $white-color;
        transition: border-color 0.15s, background 0.15s;

        &--checked,
        &--indeterminate {
            border-color: $main-orange;
            background: $main-orange;
        }
    }

    &__option-label {
        font-family: $font-family-archivo;
        font-size: 14px;
        font-weight: 400;
        color: rgba($black-color, 0.85);
        line-height: 1.3;
    }

    &__footer {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 8px;
        margin-top: 6px;
        padding: 10px 18px 0;
        border-top: 1px solid $border-gray-color;
    }

    &__footer-action {
        min-width: 76px;
        height: 34px;
        padding: 0 14px;
        border-radius: 999px;
        font-family: $font-family-archivo;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;

        &--secondary {
            border: 1px solid $border-gray-color;
            background: $white-color;
            color: rgba($black-color, 0.72);
        }

        &--primary {
            border: 1px solid transparent;
            background: $main-orange;
            color: $white-color;
        }
    }
}

:global(.p-popover.checkbox-filter-popover) {
    --p-popover-gutter: 14px;
    --p-popover-background: #{$white-color};
    --p-popover-border-color: #{$border-gray-color};
    --p-popover-border-radius: 10px;
    --p-popover-shadow: 8px 18px 60px rgba(0, 0, 0, 0.08);
    --p-popover-content-padding: 12px 0 8px;
    min-width: 240px;
}

:global(.p-popover.checkbox-filter-popover::before),
:global(.p-popover.checkbox-filter-popover::after) {
    display: none !important;
}
</style>
